// src/components/AdminDashboard/sections/UserPurchasesSection.jsx
import React, { useState, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import {
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Button,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import FilterListIcon from '@mui/icons-material/FilterList';
import {
  SectionCard,
  TableWrapper,
  StyledTable,
  StyledTableCell,
  NoDataMessage
} from '../AdminDashboard.styles';
import { media } from '../../../../utils/media';
import Pagination from '../../../Video/videoList/Pagination';
import { COLORS } from '../../../../BrandColors';

const UserPurchasesSection = ({ 
  purchases, 
  loadingPurchases, 
  purchaseError,
  currentPage,
  totalPages,
  totalElements,
  pageSize,
  useClientSidePagination,
  onPageChange,
  onPageSizeChange
}) => {
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCourt, setFilterCourt] = useState('');
  const [filterDateRange, setFilterDateRange] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  // Get unique court names for filter dropdown
  const courtNames = useMemo(() => {
    const courts = [...new Set(purchases.map(p => p.video?.courtName).filter(Boolean))];
    return courts.sort();
  }, [purchases]);

  // Filter purchases based on search and filters
  const filteredPurchases = useMemo(() => {
    let filtered = purchases;

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(purchase => 
        purchase.user?.username?.toLowerCase().includes(term) ||
        purchase.user?.email?.toLowerCase().includes(term) ||
        purchase.video?.title?.toLowerCase().includes(term) ||
        purchase.video?.courtName?.toLowerCase().includes(term) ||
        purchase.id?.toString().includes(term)
      );
    }

    // Court filter
    if (filterCourt) {
      filtered = filtered.filter(purchase => 
        purchase.video?.courtName === filterCourt
      );
    }

    // Date range filter
    if (filterDateRange !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      switch (filterDateRange) {
        case 'today':
          filtered = filtered.filter(purchase => {
            const purchaseDate = new Date(purchase.purchaseDate);
            return purchaseDate >= today;
          });
          break;
        case 'week':
          const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
          filtered = filtered.filter(purchase => {
            const purchaseDate = new Date(purchase.purchaseDate);
            return purchaseDate >= weekAgo;
          });
          break;
        case 'month':
          const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
          filtered = filtered.filter(purchase => {
            const purchaseDate = new Date(purchase.purchaseDate);
            return purchaseDate >= monthAgo;
          });
          break;
        default:
          break;
      }
    }

    return filtered;
  }, [purchases, searchTerm, filterCourt, filterDateRange]);

  // For client-side pagination, we need to paginate the filtered results
  const paginatedFilteredPurchases = useMemo(() => {
    if (!useClientSidePagination) {
      return filteredPurchases;
    }
    
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredPurchases.slice(startIndex, endIndex);
  }, [filteredPurchases, useClientSidePagination, currentPage, pageSize]);

  // Calculate total pages for filtered results
  const totalFilteredPages = useMemo(() => {
    if (!useClientSidePagination) {
      return totalPages;
    }
    return Math.ceil(filteredPurchases.length / pageSize);
  }, [filteredPurchases, useClientSidePagination, totalPages, pageSize]);

  const clearFilters = () => {
    setSearchTerm('');
    setFilterCourt('');
    setFilterDateRange('all');
  };

  const hasActiveFilters = searchTerm || filterCourt || filterDateRange !== 'all';

  const handlePageChange = (page) => {
    onPageChange(page);
  };
  

  // Reset to page 1 when filters change (for client-side pagination)
  useEffect(() => {
    if (useClientSidePagination && hasActiveFilters && currentPage !== 1) {
      onPageChange(1);
    }
  }, [searchTerm, filterCourt, filterDateRange, useClientSidePagination, hasActiveFilters, currentPage, onPageChange]);

  return (
    <SectionCard>
      <StyledAccordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
            <Typography variant="h6">User Purchases</Typography>
            <Box display="flex" alignItems="center" gap={1}>
              {hasActiveFilters && (
                <Chip 
                  label={`${filteredPurchases.length} of ${totalElements}`} 
                  color="primary" 
                  size="small" 
                />
              )}
              <Tooltip title="Toggle Filters">
                <IconButton 
                  size="small" 
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowFilters(!showFilters);
                  }}
                  color={showFilters ? "primary" : "default"}
                >
                  <FilterListIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {/* Search and Filters */}
          {showFilters && (
            <FiltersContainer>
              <SearchContainer>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Search by username, email, video title, court name, or purchase ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />,
                    endAdornment: searchTerm && (
                      <IconButton size="small" onClick={() => setSearchTerm('')}>
                        <ClearIcon />
                      </IconButton>
                    )
                  }}
                  size="small"
                />
              </SearchContainer>
              
              <FilterControls>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Court</InputLabel>
                  <Select
                    value={filterCourt}
                    onChange={(e) => setFilterCourt(e.target.value)}
                    label="Court"
                  >
                    <MenuItem value="">All Courts</MenuItem>
                    {courtNames.map(court => (
                      <MenuItem key={court} value={court}>{court}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Date Range</InputLabel>
                  <Select
                    value={filterDateRange}
                    onChange={(e) => setFilterDateRange(e.target.value)}
                    label="Date Range"
                  >
                    <MenuItem value="all">All Time</MenuItem>
                    <MenuItem value="today">Today</MenuItem>
                    <MenuItem value="week">Last 7 Days</MenuItem>
                    <MenuItem value="month">Last 30 Days</MenuItem>
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Page Size</InputLabel>
                  <Select
                    value={pageSize}
                    onChange={(e) => onPageSizeChange(e.target.value)}
                    label="Page Size"
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={15}>15</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                  </Select>
                </FormControl>

                {hasActiveFilters && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={clearFilters}
                    startIcon={<ClearIcon />}
                  >
                    Clear Filters
                  </Button>
                )}
              </FilterControls>
            </FiltersContainer>
          )}

          {/* Summary Stats */}
          <SummaryContainer>
            <Typography variant="body2" color="text.secondary">
              Showing {paginatedFilteredPurchases.length} purchases (Page {currentPage} of {totalFilteredPages})
            </Typography>
            {hasActiveFilters && (
              <Typography variant="body2" color="primary">
                Filtered from {filteredPurchases.length} purchases
              </Typography>
            )}
            {!hasActiveFilters && useClientSidePagination && (
              <Typography variant="body2" color="text.secondary">
                Total: {totalElements} purchases
              </Typography>
            )}
          </SummaryContainer>

          {/* Table */}
          <EnhancedTableWrapper>
            <StyledTable>
              <TableHead>
                <TableRow>
                  <StyledTableCell>Purchase ID</StyledTableCell>
                  <StyledTableCell>Court Name</StyledTableCell>
                  <StyledTableCell>Username</StyledTableCell>
                  <StyledTableCell>Email</StyledTableCell>
                  <StyledTableCell>Video Title</StyledTableCell>
                  <StyledTableCell>Purchase Date</StyledTableCell>
                  <StyledTableCell>Price</StyledTableCell>
                  <StyledTableCell>Video Link</StyledTableCell>
                  <StyledTableCell>Discount Code</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingPurchases ? (
                  <TableRow key="loading">
                    <TableCell colSpan={9} align="center">
                      <LoadingContainer>
                        <CircularProgress />
                      </LoadingContainer>
                    </TableCell>
                  </TableRow>
                ) : filteredPurchases.length === 0 ? (
                  <TableRow key="no-data">
                    <TableCell colSpan={9} align="center">
                      <EnhancedNoDataMessage>
                        {hasActiveFilters ? 'No purchases match your filters.' : 'No purchases available.'}
                      </EnhancedNoDataMessage>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedFilteredPurchases.map((purchase) => (
                    <TableRow key={`purchase-${purchase.id}-${purchase.purchaseDate}`}>
                      <TableCell>{purchase.id}</TableCell>
                      <TableCell>{purchase.video?.courtName || 'N/A'}</TableCell>
                      <TableCell>{purchase.user?.username || 'N/A'}</TableCell>
                      <TableCell>{purchase.user?.email || 'N/A'}</TableCell>
                      <TableCell>{purchase.video?.title || 'N/A'}</TableCell>
                      <TableCell>
                        {purchase.purchaseDate
                          ? new Date(purchase.purchaseDate).toLocaleString()
                          : 'N/A'}
                      </TableCell>
                      <TableCell>
                        {purchase.price != null
                          ? `$${purchase.price.toFixed(2)}`
                          : 'N/A'}
                      </TableCell>
                      <TableCell>
                        {purchase.video?.preSignedS3Url ? (
                          <a
                            href={purchase.video.preSignedS3Url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            View Video
                          </a>
                        ) : (
                          'N/A'
                        )}
                      </TableCell>
                      <TableCell>{purchase.discountCode || 'N/A'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </StyledTable>
            {purchaseError && <Typography color="error">{purchaseError}</Typography>}
          </EnhancedTableWrapper>

          {/* Pagination */}
          {!loadingPurchases && totalFilteredPages > 1 && (
            <PaginationContainer>
              <Pagination
                currentPage={currentPage}
                totalPages={totalFilteredPages}
                onPageChange={handlePageChange}
              />
            </PaginationContainer>
          )}
        </AccordionDetails>
      </StyledAccordion>
    </SectionCard>
  );
};

UserPurchasesSection.propTypes = {
  purchases: PropTypes.array.isRequired,
  loadingPurchases: PropTypes.bool.isRequired,
  purchaseError: PropTypes.string,
  currentPage: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  totalElements: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  useClientSidePagination: PropTypes.bool.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onPageSizeChange: PropTypes.func.isRequired
};

export default UserPurchasesSection;

/* ─────────────────────────────────────────────────────────────
   STYLED COMPONENTS
────────────────────────────────────────────────────────────── */
const FiltersContainer = styled.div`
  margin-bottom: 20px;
  padding: 20px;
  background: linear-gradient(135deg, ${COLORS.primaryBackground} 0%, #f8f9fa 100%);
  border-radius: 12px;
  border: 2px solid ${COLORS.accentTeal}20;
  box-shadow: 0 4px 12px rgba(0, 166, 147, 0.1);
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 6px 20px rgba(0, 166, 147, 0.15);
    border-color: ${COLORS.accentTeal}40;
  }
`;

const SearchContainer = styled.div`
  margin-bottom: 16px;

  .MuiTextField-root {
    .MuiOutlinedInput-root {
      border-radius: 8px;
      border-color: ${COLORS.accentBlueGray}40;
      
      &:hover {
        border-color: ${COLORS.accentTeal};
      }
      
      &.Mui-focused {
        border-color: ${COLORS.accentTeal};
        box-shadow: 0 0 0 2px ${COLORS.accentTeal}20;
      }
    }
  }
`;

const FilterControls = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
  
  .MuiFormControl-root {
    .MuiOutlinedInput-root {
      border-radius: 8px;
      border-color: ${COLORS.accentBlueGray}40;
      
      &:hover {
        border-color: ${COLORS.accentTeal};
      }
      
      &.Mui-focused {
        border-color: ${COLORS.accentTeal};
        box-shadow: 0 0 0 2px ${COLORS.accentTeal}20;
      }
    }
  }

  .MuiButton-outlined {
    border-color: ${COLORS.secondaryButton};
    color: ${COLORS.secondaryButton};
    border-radius: 8px;
    font-weight: 600;
    transition: all 0.3s ease;
    
    &:hover {
      background-color: ${COLORS.secondaryButton};
      color: white;
      border-color: ${COLORS.secondaryButton};
      transform: translateY(-1px);
      box-shadow: 0 4px 8px rgba(189, 32, 38, 0.3);
    }
  }
  
  ${media.mobile`
    flex-direction: column;
    align-items: stretch;
  `}
`;

const SummaryContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 16px;
  background: linear-gradient(90deg, ${COLORS.accentTeal}10 0%, ${COLORS.accentOrange}10 100%);
  border-radius: 8px;
  border-left: 4px solid ${COLORS.accentTeal};
  
  .MuiTypography-root {
    color: ${COLORS.accentBlueGray};
    font-weight: 500;
    
    &.MuiTypography-colorPrimary {
      color: ${COLORS.accentTeal};
      font-weight: 600;
    }
  }
  
  ${media.mobile`
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  `}
`;

const PaginationContainer = styled.div`
  margin-top: 24px;
  display: flex;
  justify-content: center;
  padding: 16px;
  background-color: ${COLORS.primaryBackground};
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

// Enhanced table styling
const EnhancedTableWrapper = styled(TableWrapper)`
  .MuiTable-root {
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    
    .MuiTableHead-root {
      background: linear-gradient(135deg, ${COLORS.accentTeal} 0%, ${COLORS.accentBlueGray} 100%);
      
      .MuiTableCell-head {
        color: white;
        font-weight: 700;
        font-size: 0.9rem;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        border-bottom: none;
      }
    }
    
    .MuiTableBody-root {
      .MuiTableRow-root {
        transition: background-color 0.2s ease;
        
        &:hover {
          background-color: ${COLORS.accentTeal}08;
        }
        
        &:nth-of-type(even) {
          background-color: ${COLORS.primaryBackground};
        }
        
        .MuiTableCell-body {
          border-bottom: 1px solid ${COLORS.accentBlueGray}20;
          color: ${COLORS.accentBlueGray};
          font-weight: 500;
          
          a {
            color: ${COLORS.accentTeal};
            text-decoration: none;
            font-weight: 600;
            transition: color 0.2s ease;
            
            &:hover {
              color: ${COLORS.accentOrange};
              text-decoration: underline;
            }
          }
        }
      }
    }
  }
`;

// Enhanced accordion styling
const StyledAccordion = styled(Accordion)`
  border-radius: 12px !important;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1) !important;
  border: 1px solid ${COLORS.accentTeal}20;
  
  .MuiAccordionSummary-root {
    background: linear-gradient(135deg, ${COLORS.primaryBackground} 0%, ${COLORS.accentTeal}05 100%);
    border-bottom: 2px solid ${COLORS.accentTeal}20;
    
    .MuiAccordionSummary-content {
      .MuiTypography-h6 {
        color: ${COLORS.accentBlueGray};
        font-weight: 700;
        font-size: 1.2rem;
      }
      
      .MuiChip-root {
        background-color: ${COLORS.accentTeal};
        color: white;
        font-weight: 600;
      }
      
      .MuiIconButton-root {
        color: ${COLORS.accentTeal};
        
        &:hover {
          background-color: ${COLORS.accentTeal}10;
        }
      }
    }
  }
  
  .MuiAccordionDetails-root {
    background-color: ${COLORS.primaryBackground};
    padding: 24px;
  }
`;

// Enhanced loading and no data styling
const EnhancedNoDataMessage = styled(NoDataMessage)`
  color: ${COLORS.accentBlueGray};
  font-size: 1.1rem;
  font-weight: 500;
  padding: 40px 20px;
  background: linear-gradient(135deg, ${COLORS.accentTeal}05 0%, ${COLORS.accentOrange}05 100%);
  border-radius: 8px;
  border: 2px dashed ${COLORS.accentTeal}40;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
  
  .MuiCircularProgress-root {
    color: ${COLORS.accentTeal};
  }
`;
